/**
 * Presentation helpers for the session portal (issue #321 Milestone 1).
 *
 * EVERY FUNCTION HERE IS A LOOKUP, NOT A DECISION. They turn a server-derived
 * enum into words. None of them inspects a URL, compares a clock, or infers a
 * capability — those are the five duplicated policy engines this milestone
 * removes, and re-growing one here in the name of "just formatting" is how it
 * would come back.
 *
 * The test `session-portal.test.ts` asserts totality: adding a state or issue
 * to the server contract without adding its sentence here fails at compile
 * time via `Record<...>`, and fails loudly at runtime for a value the server
 * introduces ahead of the client.
 */
import type {
  ClockPhase,
  DestinationIssue,
  DestinationState,
  SessionAction,
  WorkspaceSession,
  SessionPublicChange,
} from "@/lib/api/sessionPortal";

export function sessionChangeWords(change: SessionPublicChange | null | undefined, includeNote = true): string | null {
  if (!change) return null;
  const date = change.from_date ? new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${change.from_date}T12:00:00Z`)) : null;
  const lead = change.kind === "cancel" ? "Cancelled" : change.kind === "reinstate" ? "Reinstated" : `Moved from ${date}`;
  return includeNote ? `${lead} · ${change.note}` : lead;
}
import type { AttendanceStatus, SessionStatus } from "@/lib/api/attendance";

/**
 * Display phase. Never "Live" — the portal does not know that Google is up.
 * A passed session with nothing recorded has no phase word: it is said once,
 * where its outcome is recorded (the teacher's roll, the learner's own
 * attendance).
 */
export const CLOCK_PHASE_LABEL: Record<ClockPhase, string | null> = {
  upcoming: "Upcoming",
  scheduled_now: "Scheduled now",
  status_pending: null,
};

export const SESSION_STATUS_LABEL: Record<SessionStatus, string> = {
  scheduled: "Scheduled",
  held: "Held",
  cancelled: "Cancelled",
};

/**
 * Which of the page's three standing regions leads THIS render.
 *
 * Still a lookup. The session hour is a sequence — readiness, then the hour
 * itself, then the record — and a page that gives all three the same weight
 * makes the reader find their own place in it. This decides emphasis and
 * disclosure only: the regions, their order, and their anchors are identical
 * in every phase, because a reader who scrolled to `#after-session-heading`
 * before session must still land on it.
 *
 * It reads no clock. `status` and `clock_phase` are both server-derived from
 * the database's `now()`, and the two total maps below are the whole rule.
 */
export type ClassPhase = "before" | "during" | "after" | "cancelled";

/** The institutional state wins when it has an answer of its own. */
const PHASE_BY_STATUS: Record<SessionStatus, ClassPhase | null> = {
  // A cancelled session is not "before" anything. It keeps its original time
  // visible and asks for nothing.
  cancelled: "cancelled",
  held: "after",
  // Still scheduled: the display clock decides, below.
  scheduled: null,
};

const PHASE_BY_CLOCK: Record<ClockPhase, ClassPhase> = {
  upcoming: "before",
  scheduled_now: "during",
  // The window has passed but nobody has recorded an outcome. That is the
  // start of the record, not the end of the hour.
  status_pending: "after",
};

export function classPhase(
  session: Pick<WorkspaceSession, "status" | "clock_phase">,
): ClassPhase {
  return PHASE_BY_STATUS[session.status] ?? PHASE_BY_CLOCK[session.clock_phase];
}

/**
 * A recorded mark, in words. `AttendanceStatus` is a stored enum, and the
 * portal rendered it raw — a learner read "Your attendance: present", which is
 * the database's word for it, not the institution's.
 *
 * There is deliberately no entry for "not recorded": absence of a row is not a
 * status, and giving it a label here is the first step toward rendering it as
 * one. The caller states that case in its own sentence.
 */
export const ATTENDANCE_STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: "Present",
  absent: "Absent",
  excused: "Excused",
  late: "Late",
};

/**
 * The order the after-session summary reads its counts in: the roll as a teacher
 * says it, not the enum's declaration order.
 */
export const ATTENDANCE_STATUS_ORDER: AttendanceStatus[] = [
  "present",
  "late",
  "excused",
  "absent",
];

/**
 * What a LEARNER is told. Deliberately short of a reason: the design is
 * explicit that a learner-facing failure state exposes no editor, no
 * provenance, and no diagnosis of someone else's configuration.
 */
export const LEARNER_DESTINATION_MESSAGE: Record<DestinationState, string | null> = {
  not_applicable: null,
  ready: null,
  missing_url: "Meeting link not available yet",
  invalid_configuration: "Meeting link needs correction",
};

/**
 * What ASSIGNED STAFF are told: the exact repair, because they are the person
 * who can perform it and a vague "something is wrong" costs a support round
 * trip during the five minutes before session.
 */
export const STAFF_DESTINATION_ISSUE: Record<DestinationIssue, string> = {
  provider_not_enabled:
    "This section's meeting provider is no longer enabled for your institution. Choose a currently enabled provider.",
  unsafe_url:
    "The meeting link is not a usable https:// address. Paste the full link from the provider.",
  embedded_credentials:
    "The meeting link carries a username or password in the address. Paste the plain meeting link instead.",
  missing_meeting_path:
    "The link points at the provider's home page rather than a meeting. Paste the full meeting link.",
  provider_host_mismatch:
    "The link does not belong to the selected provider. Correct the link, or change the provider to match it.",
  provider_not_remote:
    "An online or hybrid session cannot use the in-person provider. Choose a remote provider, or set the modality to in person.",
  remote_provider_for_in_person:
    "This session meets in person but still carries a remote link. Learners are not shown it; clear it when convenient.",
};

/**
 * The label on the one phase-appropriate primary action, in the user's words
 * (sprint 1, decision Q2): a teacher starts the session and takes the roll.
 */
export const ACTION_LABEL: Record<SessionAction, string> = {
  join_meeting: "Start session",
  set_meeting_link: "Set meeting link",
  fix_meeting_link: "Fix meeting link",
  create_google_meet: "Create Google Meet",
  replace_google_meet: "Replace Google Meet",
  create_zoom_meeting: "Create Zoom meeting",
  replace_zoom_meeting: "Replace Zoom meeting",
  clear_session_override: "Use course default",
  create_session_plan: "Add session plan",
  edit_session_plan: "Edit session plan",
  take_attendance: "Take the roll",
  complete_attendance: "Submit roll",
  close_session: "Close session",
  update_roll: "Update roll",
  publish_session_plan: "Review and publish",
  review_preparation: "Review preparation",
  view_location: "View location",
  view_course: "Open course",
  // Issue #391 Milestone 3. None of these three render through the generic
  // `ActionControl` switch in SessionPortalPage.tsx — the coursework regions
  // consume them directly (`manage_session_coursework` gates the per-unit
  // adding of materials and work on the session, in place; the other two are read-only
  // signals with no distinct control today) — but `ACTION_LABEL` is a total
  // `Record<SessionAction, string>`, so every id still needs an entry here.
  publish_session_resource: "Publish coursework",
  manage_session_coursework: "Manage coursework",
  open_session_activity: "Review and hand in",
  cancel_session: "Cancel session",
  move_session: "Move session",
  reinstate_session: "Reinstate session",
};

/**
 * A learner's label where it differs from the teacher's: a learner joins a
 * session rather than starting it. The server's `join_label` still wins where
 * it names a destination.
 */
export const LEARNER_ACTION_LABEL: Partial<Record<SessionAction, string>> = {
  join_meeting: "Join session",
};

/** The act's label for this viewer. A lookup, never a decision about the act. */
export function actionLabel(action: SessionAction, teaching: boolean): string {
  return (teaching ? undefined : LEARNER_ACTION_LABEL[action]) ?? ACTION_LABEL[action];
}

/**
 * Component fields equivalent to each `dateStyle`, spelled out explicitly.
 *
 * `Intl.DateTimeFormat` refuses to mix `dateStyle`/`timeStyle` with an
 * explicit component option such as `timeZoneName` in the SAME call — it
 * throws `TypeError: Invalid option : option` rather than silently choosing
 * one family. `formatInCourseZone` still accepts the friendly `dateStyle`
 * shorthand from its callers, but before anything reaches
 * `Intl.DateTimeFormat` it is expanded into these components and merged with
 * whatever explicit fields (including `timeZoneName`) the caller supplied, so
 * the two mutually exclusive families never appear in one constructor call.
 * This table is total — TypeScript enforces every `dateStyle` value maps to
 * something — so a future style added upstream cannot fall through untranslated.
 */
const DATE_STYLE_COMPONENTS: Record<
  NonNullable<Intl.DateTimeFormatOptions["dateStyle"]>,
  Intl.DateTimeFormatOptions
> = {
  full: { weekday: "long", year: "numeric", month: "long", day: "numeric" },
  long: { year: "numeric", month: "long", day: "numeric" },
  medium: { year: "numeric", month: "short", day: "numeric" },
  short: { year: "2-digit", month: "numeric", day: "numeric" },
};

/**
 * Component fields equivalent to each `timeStyle` — deliberately WITHOUT
 * seconds. `Intl`'s own `timeStyle: "medium"` and above would add `:ss`; a
 * scheduled session start never carries second-level precision, so every entry
 * here stops at `hour`/`minute` regardless of which style name was asked for.
 */
const TIME_STYLE_COMPONENTS: Record<
  NonNullable<Intl.DateTimeFormatOptions["timeStyle"]>,
  Intl.DateTimeFormatOptions
> = {
  full: { hour: "numeric", minute: "2-digit" },
  long: { hour: "numeric", minute: "2-digit" },
  medium: { hour: "numeric", minute: "2-digit" },
  short: { hour: "numeric", minute: "2-digit" },
};

/**
 * Format an instant in the COURSE's zone, with the zone named.
 *
 * The browser's zone never decides anything here. A learner travelling, or an
 * instructor covering a section in another region, must read the same wall
 * clock the institution scheduled, and the abbreviation is what makes that
 * legible rather than merely correct.
 *
 * Callers may still pass `dateStyle`/`timeStyle` for convenience — see
 * `DATE_STYLE_COMPONENTS`/`TIME_STYLE_COMPONENTS` above for why those never
 * reach `Intl.DateTimeFormat` directly. Pass `timeZoneName` explicitly to get
 * the abbreviation; it is not inferred from `timeStyle`, because a caller
 * formatting a RANGE (see `formatSessionWindow`) wants it on only one side.
 */
export function formatInCourseZone(
  instant: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "full",
    timeStyle: "short",
    timeZoneName: "short",
  },
): string {
  const { dateStyle, timeStyle, ...explicit } = options;
  const resolved: Intl.DateTimeFormatOptions = {
    ...(dateStyle ? DATE_STYLE_COMPONENTS[dateStyle] : {}),
    ...(timeStyle ? TIME_STYLE_COMPONENTS[timeStyle] : {}),
    // The caller's own explicit fields — including `timeZoneName` — win over
    // the expanded shorthand, so a single option object can still ask for the
    // zone abbreviation or override one component without conflict.
    ...explicit,
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat(undefined, resolved).format(new Date(instant));
  } catch {
    // An unknown zone must not blank the header. Migration 061 constrains
    // `lms_course.timezone` to a region name, so reaching here means the
    // browser's IANA database lags the stored zone, not that the data is bad.
    // The former fallback (`new Date(instant).toLocaleString()`) rendered the
    // wall clock in the BROWSER's zone while giving no sign it had done so —
    // exactly the bug this function exists to prevent. Render the true
    // instant in UTC instead, and say so, rather than imply `timeZone` was
    // used.
    const utc = new Date(instant).toISOString().slice(0, 16).replace("T", " ");
    return `${utc} UTC`;
  }
}

/**
 * `10:00 to 11:30 AM` in the course's zone (ADR 0035, decision 8): one
 * meridiem when both ends share it, `11:30 AM to 1:00 PM` otherwise, no dash
 * and no zone. The zone is named once, in the meta line that uses the range
 * (`zoneAbbreviation` gives it to a consumer that must say it per row).
 */
export function formatSessionWindow(
  startsAt: string,
  endsAt: string,
  timeZone: string,
): string {
  const start = formatTimeFigure(startsAt, timeZone);
  const end = formatTimeFigure(endsAt, timeZone);
  if (start.period && end.period && start.period === end.period) {
    return `${start.figure} to ${end.figure} ${end.period}`;
  }
  const say = (part: { figure: string; period: string | null }) =>
    part.period ? `${part.figure} ${part.period}` : part.figure;
  return `${say(start)} to ${say(end)}`;
}

/** The zone's short name for an instant (`EDT`, `GMT+9`), or the IANA name when the browser cannot abbreviate it. */
export function zoneAbbreviation(instant: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: "short" })
      .formatToParts(new Date(instant))
      .find((part) => part.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return "UTC";
  }
}

/**
 * A session's start as the time tile's figure: `{ figure: "2:00", period: "PM" }`
 * in the course's zone. The period is split out so the tile can set it smaller
 * beside the numerals; a 24-hour locale returns no period.
 *
 * Formatting only. The tile states when the session starts; it never compares
 * that instant with the reader's clock (ADR 0016).
 */
export function formatTimeFigure(
  instant: string,
  timeZone: string,
): { figure: string; period: string | null } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone,
    }).formatToParts(new Date(instant));
  } catch {
    return { figure: formatInCourseZone(instant, timeZone), period: null };
  }
  const period = parts.find((part) => part.type === "dayPeriod")?.value ?? null;
  const figure = parts
    .filter((part) => part.type !== "dayPeriod")
    .map((part) => part.value)
    .join("")
    .trim();
  return { figure, period };
}
